<template>
  <div class="management-page flex flex-col" data-testid="management-page">
    <div class="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-[24px] font-bold text-[var(--color-dark)] m-0 leading-tight">
          {{ $t('UserProjects.management.title') }}
        </h1>
        <p class="text-sm text-[var(--color-disabled)] mt-1 mb-0">
          {{ $t('UserProjects.management.subtitle') }}
        </p>
      </div>
      <Button
        :label="$t('UserProjects.management.refresh')"
        icon="pi pi-refresh"
        severity="secondary"
        :loading="loading"
        data-testid="management-refresh"
        @click="refresh"
      />
    </div>

    <Message v-if="fatalError" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="loading && !portfolio"
      class="flex items-center justify-center py-16 text-[var(--color-disabled)]"
      data-testid="management-loading"
    >
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.management.loading') }}
    </div>

    <template v-else-if="portfolio">
      <ManagementDecisionFocusGrid
        :items="decisionFocus"
        @view-all-decisions="openFirstProjectOverview"
      />
      <ManagementPortfolioRiskTable
        :rows="portfolioRisk"
        @select-project="openProjectOverview"
      />
      <ManagementPortfolioComparison
        :comparison="comparison"
        @select-project="openProjectOverview"
      />
      <ManagementInsightsStrip :insights="insights" />
      <ManagementBlockagesPanel :blockages="blockages" />
      <ManagementDeepDiveActions @navigate="navigateDeepDive" />
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ManagementDecisionFocusGrid from '@/components/CustomerApp/UserProjects/ManagementDecisionFocusGrid.vue';
import ManagementPortfolioRiskTable from '@/components/CustomerApp/UserProjects/ManagementPortfolioRiskTable.vue';
import ManagementPortfolioComparison from '@/components/CustomerApp/UserProjects/ManagementPortfolioComparison.vue';
import ManagementInsightsStrip from '@/components/CustomerApp/UserProjects/ManagementInsightsStrip.vue';
import ManagementBlockagesPanel from '@/components/CustomerApp/UserProjects/ManagementBlockagesPanel.vue';
import ManagementDeepDiveActions from '@/components/CustomerApp/UserProjects/ManagementDeepDiveActions.vue';

export default {
  name: 'UserProjectsManagementView',
  components: {
    Button,
    Message,
    ManagementDecisionFocusGrid,
    ManagementPortfolioRiskTable,
    ManagementPortfolioComparison,
    ManagementInsightsStrip,
    ManagementBlockagesPanel,
    ManagementDeepDiveActions,
  },
  computed: {
    ...mapGetters('pmManagement', [
      'loading',
      'error',
      'portfolio',
      'decisionFocus',
      'portfolioRisk',
      'comparison',
      'insights',
      'blockages',
    ]),
    fatalError() {
      return Boolean(this.error);
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    ...mapActions('pmManagement', ['fetchPortfolio']),
    ...mapActions('pmProject', ['changeUserProjectContext']),
    async refresh() {
      try {
        await this.fetchPortfolio();
      } catch {
        // error stored in vuex
      }
    },
    async openProjectOverview(projectId) {
      if (!projectId) return;
      await this.changeUserProjectContext(projectId);
      await this.$router.push('/user-projects/overview');
    },
    async navigateDeepDive(route) {
      const firstProjectId = this.portfolioRisk[0]?.projectId;
      if (firstProjectId) {
        await this.changeUserProjectContext(firstProjectId);
      }
      await this.$router.push(route);
    },
    openFirstProjectOverview() {
      this.$router.push('/user-projects/decisions');
    },
  },
};
</script>
